const getters = {
	/**
	 * Retorna o ID de sinais de tendencia selecionadas.
	 * @returns {array} Uma lista dos IDs de sinais de tendências selecionadas.
	 */
	selectedTrendSignalsSettingsIds(state)
	{
		return state.selectedTrendSignalsSettings.map((el) => el.trendSettingId);
	},

	/**
	 * Retorna os sinais de tendencia disponíveis.
	 * @returns {array} Uma lista dos IDs de sinais de tendências disponíveis.
	 */
	availableTrendSignalSettings(state)
	{
		return state.assetsTrendSettings[0]?.commonSignals ?? [];
	},
	/**
	 * Retorna os sinais de tendência atualmente selecionados.
	 * @returns {Array<Object>} Lista de sinais selecionados.
	 */
	selectedTrendSignalsSettings(state)
	{
		return state.selectedTrendSignalsSettings;
	},

	/**
	 * Verifica se os sinais de tendencias foram selecionados.
	 * @returns {boolean} - True se os sinais de tendência foram selecionados.
	 */
	isTrendSignalsSelectedValid(state)
	{
		return !!state.selectedTrendSignalsSettings && state.selectedTrendSignalsSettings.length > 0;
	},

	/**
	 * Verifica se um sinal está selecionado.
	 * @returns {function} - Função que recebe um sinal e retorna boolean.
	 */
	isSelected: (state) => (signal) =>
	{
		const signalAsset = signal.asset || null;

		if (!Array.isArray(state.selectedTrendSignalsSettings)) return false;

		return state.selectedTrendSignalsSettings.some(
			(s) => s.name === signal.name && (s.asset || null) === signalAsset
		);
	},
	/**
	 * Conta quantos sinais comuns estão selecionados.
	 * @returns {number} Quantidade de sinais comuns selecionados.
	 */
	commonSelectedCount: (state, getters, rootState) =>
	{
		return new Set(
			state.selectedTrendSignalsSettings
				.filter(s => rootState.Trend.TrendMenu.TrendSignalSettings.availableCommonSignals
					.some(common => common.name === s.name))
				.map(s => s.name)
		).size;
	},
	/**
	 * Conta quantos sinais distintos estão selecionados.
	 * @returns {number} Quantidade de sinais distintos selecionados.
	 */
	distinctSelectedCount: (state, getters, rootState, rootGetters) =>
	{
		const distinctGroups = rootState.Trend.TrendMenu.TrendSignalSettings.availableDistinctSignalGroups;
		let count = 0;
		distinctGroups.forEach(group =>
		{
			group.signals.forEach(signal =>
			{
				if (getters.isSelected(signal))
				{
					count++;
				}
			});
		});
		return count;
	},

	/**
 * Retorna o ícone a ser exibido para indicar a expansão do grupo de sinais comuns.
 * @returns {string} Nome do ícone do Material Design.
 */
	isCommonGroupIconExpanded: (state) =>
	{
		return state.CommonGroupExpanded ? "mdi-chevron-up" : "mdi-chevron-down";
	},

	/**
 * Retorna o ícone a ser exibido para indicar a expansão do grupo de sinais distintos.
 * @returns {string} Nome do ícone do Material Design.
 */
	isDistinctGroupIconExpanded: (state) =>
	{
		return state.DistinctGroupExpanded ? "mdi-chevron-up" : "mdi-chevron-down";
	},

	/**
	 * Retorna todos os grupos de ativos distintos expandidos.
	 * @returns {Array<string>} Array com os nomes dos ativos expandidos.
	 */
	expandedDistinctAssetGroups: (state) => state.expandedDistinctAssetGroups,

	 /**
   * Retorna os nomes únicos dos sinais selecionados.
   * @returns {Array} Nomes únicos.
   */
	uniqueSignalNames: (state) =>
	{
		const signals = state.selectedTrendSignalsSettings || [];
		const seen = new Set();
		signals.forEach(s => seen.add(s.name));
		return Array.from(seen);
	},

	/**
   * Retorna os primeiros dois sinais selecionados, com nomes únicos por nome.
   * @returns {Array} Até 2 sinais únicos por nome.
   */
	firstTwoSelectedSignals: (state) =>
	{
		const signals = state.selectedTrendSignalsSettings || [];
		const uniqueByName = [];
		const seenNames = new Set();

		for (const s of signals)
		{
			if (!seenNames.has(s.name))
			{
				uniqueByName.push(s);
				seenNames.add(s.name);
			}
		}

		return uniqueByName.slice(0, 2);
	},

	/**
	 * Indica se o menu está aberto.
	 * @returns {boolean} true se o menu estiver aberto, false caso contrário.
	 */
	menuOpened: (state) => state.menuOpened,
	/**
	 * Retorna os sinais comuns filtrados pelo texto do filtro.
	 * Se o filtro estiver vazio, retorna todos os sinais comuns disponíveis.
	 * @returns {Array} Lista de sinais comuns filtrados.
	 */
	filteredCommonSignals(state)
	{
		const deduplicate = (signals) =>
		{
			const seen = new Set();
			return signals.filter(s =>
			{
				const key = `${s.name}-${s.asset || ""}`;
				if (seen.has(key)) return false;
				seen.add(key);
				return true;
			});
		};

		let signals = state.availableCommonSignals;

		if (state.filterText)
		{
			const text = state.filterText.toLowerCase();
			signals = signals.filter(signal => signal.name.toLowerCase().includes(text));
		}

		return deduplicate(signals);
	},

	/**
	 * Retorna os grupos de sinais distintos filtrados pelo texto do filtro.
	 * Cada grupo terá apenas os sinais cujo nome contenha o texto do filtro.
	 * Se o filtro estiver vazio, retorna todos os grupos de sinais distintos disponíveis.
	 * Grupos sem sinais filtrados são excluídos.
	 * @returns {Array} Lista de grupos de sinais distintos filtrados.
	 */
	filteredDistinctSignalGroups(state)
	{
		const deduplicate = (signals) =>
		{
			const seen = new Set();
			return signals.filter(s =>
			{
				const key = `${s.name}-${s.asset || ""}`;
				if (seen.has(key)) return false;
				seen.add(key);
				return true;
			});
		};

		const filterByText = (signals, text) =>
		{
			const lower = text.toLowerCase();
			return signals.filter(s => s.name.toLowerCase().includes(lower));
		};

		return state.availableDistinctSignalGroups
			.map(group =>
			{
				let signals = group.signals;
				if (state.filterText)
				{
					signals = filterByText(signals, state.filterText);
				}
				return {
					...group,
					signals: deduplicate(signals)
				};
			})
			.filter(group => group.signals.length > 0);
	}

};

export default { getters };
